/**
 * Lexical Node Type Constants
 *
 * These constants define all valid node types in the Lexical editor JSON format.
 * Using constants prevents typos and makes refactoring easier.
 */

// Basic node types
export const NODE_TYPE = {
  TEXT: "text",
  PARAGRAPH: "paragraph",
  HEADING: "heading",
} as const;

// Special entity nodes
export const NODE_TYPE_ENTITY = {
  STOCK: "stock",
  TAG: "tag",
  IMAGE: "image",
} as const;

// Formatting nodes
export const NODE_TYPE_FORMAT = {
  LINK: "link",
  CODE: "code",
  CODE_HIGHLIGHT: "codehighlight",
} as const;

// Code block
export const NODE_TYPE_CODE_BLOCK = "codeblock";

// Structured content nodes
export const NODE_TYPE_STRUCTURED = {
  CHECKLIST: "checklist",
} as const;

// Investment-related nodes
export const NODE_TYPE_INVESTMENT = {
  PRICE_TREND: "price-trend",
  ACTION_ITEM: "action-item",
  SELF_CONTRACT: "self-contract",
  DECISION: "decision",
  INVESTMENT_DECISION: "investment-decision",
} as const;

// Sentiment constants for stock nodes
export const STOCK_SENTIMENT = {
  BULLISH: "bullish",
  BEARISH: "bearish",
  NEUTRAL: "neutral",
} as const;

// List type constants
export const LIST_TYPE = {
  BULLET: "bullet",
  NUMBER: "number",
  CHECK: "check",
} as const;

// Quote type constants
export const QUOTE_TYPE = {
  BLOCK: "block",
} as const;

// Combined type for type checking
export const NODE_TYPES = {
  ...NODE_TYPE,
  ...NODE_TYPE_ENTITY,
  ...NODE_TYPE_FORMAT,
  ...NODE_TYPE_STRUCTURED,
  ...NODE_TYPE_INVESTMENT,
  CODEBLOCK: NODE_TYPE_CODE_BLOCK,
} as const;

/**
 * 包含 ticker 的节点类型集合
 * 用于提取和统一刷新 ticker 缓存
 */
export const TICKER_NODE_TYPES = new Set<string>([
  NODE_TYPE_ENTITY.STOCK,
  NODE_TYPE_INVESTMENT.SELF_CONTRACT,
  NODE_TYPE_INVESTMENT.DECISION,
]);

/**
 * 检查节点类型是否包含 ticker
 * @param nodeType - 节点类型字符串
 * @returns 是否包含 ticker
 */
export function isTickerNodeType(nodeType: string): boolean {
  return TICKER_NODE_TYPES.has(nodeType);
}

// Type definitions
export type StockSentiment = (typeof STOCK_SENTIMENT)[keyof typeof STOCK_SENTIMENT];
export type ListType = (typeof LIST_TYPE)[keyof typeof LIST_TYPE];
export type QuoteType = (typeof QUOTE_TYPE)[keyof typeof QUOTE_TYPE];
export type NodeType = (typeof NODE_TYPES)[keyof typeof NODE_TYPES];

// Self-Contract Constants
export const CONTRACT_TEMPLATE = {
  VALUE_ORIENTED: "value-oriented",
  TRADING_ORIENTED: "trading-oriented",
} as const;

export const TRIGGER_TYPE = {
  PRICE: "price",
  EXPIRATION: "expiration",
  TIME: "time",
  CUSTOM: "custom",
} as const;

export const CONTRACT_STATUS = {
  ACTIVE: "active",
  TRIGGERED: "triggered",
  CANCELLED: "cancelled",
  EXPIRED: "expired",
} as const;

export const TRIGGER_OPERATOR = {
  GREATER_THAN_OR_EQUAL: ">=",
  LESS_THAN_OR_EQUAL: "<=",
  EQUAL: "==",
  GREATER_THAN: ">",
  LESS_THAN: "<",
} as const;

// Self-Contract Type definitions
export type ContractTemplate = (typeof CONTRACT_TEMPLATE)[keyof typeof CONTRACT_TEMPLATE];
export type TriggerType = (typeof TRIGGER_TYPE)[keyof typeof TRIGGER_TYPE];
export type ContractStatus = (typeof CONTRACT_STATUS)[keyof typeof CONTRACT_STATUS];
export type TriggerOperator = (typeof TRIGGER_OPERATOR)[keyof typeof TRIGGER_OPERATOR];

// Decision Constants
export const DECISION_TYPE = {
  BUY: "BUY",
  SELL: "SELL",
  HOLD: "HOLD",
} as const;

export const DECISION_ACTION_TYPE = {
  FOLLOW: "FOLLOW",
  OVERRIDE: "OVERRIDE",
  NONE: "NONE",
} as const;

export const DECISION_STATUS = {
  ACTIVE: "active",
  TRIGGERED: "triggered",
  CANCELLED: "cancelled",
  EXPIRED: "expired",
} as const;

export type DecisionType = (typeof DECISION_TYPE)[keyof typeof DECISION_TYPE];
export type DecisionActionType = (typeof DECISION_ACTION_TYPE)[keyof typeof DECISION_ACTION_TYPE];
